import { useCallback, useEffect, useState } from 'react';

import { useDocumentVisibility } from '~/lib/hooks/useDocumentVisibility';
import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import { wrapAround } from '~/lib/utils/math';

interface UseCarouselProps {
  total: number;
  initialIndex?: number;
  autoplayDelay?: number;
}

export const useCarousel = ({
  total,
  initialIndex = 0,
  autoplayDelay = 1000,
}: UseCarouselProps) => {
  const [index, setIndex] = useState<number>(initialIndex);
  const [isPaused, setPaused] = useState<boolean>(false);
  const [isStopped, setStopped] = useState<boolean>(false);

  const isDocumentVisible = useDocumentVisibility();
  const prefersReducedMotion = useHydrationSafeResponsive(
    'prefers-reduced-motion'
  );

  const increment = useCallback(
    (bypass: boolean = false) => {
      if (!bypass) {
        setStopped(true);
      }

      setIndex(index + 1);
    },
    [index]
  );

  const decrement = useCallback(
    (bypass: boolean = false) => {
      if (!bypass) {
        setStopped(true);
      }

      setIndex(index - 1);
    },
    [index]
  );

  const start = useCallback((bypass: boolean = false) => {
    setPaused(false);

    if (bypass) {
      setStopped(false);
    }
  }, []);

  const pause = useCallback((bypass: boolean = false) => {
    setPaused(true);

    if (bypass) {
      setStopped(true);
    }
  }, []);

  useEffect(() => {
    if (!isPaused && !isStopped && isDocumentVisible) {
      const timeoutId = setTimeout(() => increment(true), autoplayDelay);

      return () => clearTimeout(timeoutId);
    }
  }, [autoplayDelay, increment, isDocumentVisible, isPaused, isStopped]);

  useEffect(() => {
    if (prefersReducedMotion) {
      pause();
    } else {
      start();
    }
  }, [pause, prefersReducedMotion, start]);

  return {
    realIndex: index,
    index: wrapAround(index, 0, total),
    isPaused,
    isStopped,
    increment,
    decrement,
    start,
    pause,
  };
};
